import React, { useState, useEffect } from "react";
import {
  Card,
  CardContent,
  CardFooter,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { format, formatDuration, intervalToDuration, Duration } from "date-fns";
import { Clock, AlertCircle } from "lucide-react";
import { useAuthStore } from "@/store/authStore";
import { useAttendanceStore } from "@/store/attendanceStore";
import { AttendanceType, StaffShift } from "@/models/attendanceModel";
import { toast } from "@/hooks/use-toast";
import { useTranslation } from "react-i18next";

interface AttendanceClockCardProps {
  storeId: string;
  staffId: string;
}

export const AttendanceClockCard = ({
  storeId,
  staffId,
}: AttendanceClockCardProps) => {
  const { t } = useTranslation();
  const {
    fetchCurrentAttendance,
    currentAttendance,
    clockIn,
    clockOut,
    fetchCurrentShift,
    currentShift,
  } = useAttendanceStore();
  const [currentTime, setCurrentTime] = useState(new Date());
  const [elapsedTime, setElapsedTime] = useState<string>("");
  const [shiftCountdown, setShiftCountdown] = useState<string | null>(null);
  const [shiftStatus, setShiftStatus] = useState<
    "before" | "during" | "after" | "none"
  >("none");

  // Format the time string for display
  const formatTimeString = (duration: Duration) => {
    return formatDuration(duration, {
      format: ["hours", "minutes", "seconds"],
    }).replace(/,\s*/g, " ");
  };

  // Initialize data
  useEffect(() => {
    fetchCurrentAttendance(staffId);
    fetchCurrentShift(staffId);

    // Update current time every second
    const intervalId = setInterval(() => {
      setCurrentTime(new Date());
    }, 1000);

    return () => clearInterval(intervalId);
  }, [fetchCurrentAttendance, fetchCurrentShift, staffId]);

  // Calculate elapsed time since clock-in
  useEffect(() => {
    if (currentAttendance?.clock_in) {
      const intervalId = setInterval(() => {
        const now = new Date();
        const clockInDate = new Date(currentAttendance.clock_in);
        const duration = intervalToDuration({
          start: clockInDate,
          end: now,
        });

        setElapsedTime(formatTimeString(duration));
      }, 1000);

      return () => clearInterval(intervalId);
    } else {
      setElapsedTime("");
    }
  }, [currentAttendance]);

  // Calculate shift countdown
  useEffect(() => {
    if (!currentShift) {
      setShiftCountdown(null);
      setShiftStatus("none");
      return;
    }

    const intervalId = setInterval(() => {
      const now = new Date();
      const currentHour = now.getHours();
      const currentMinute = now.getMinutes();
      const currentTimeMinutes = currentHour * 60 + currentMinute;

      // Parse shift times
      const [startHour, startMinute] = currentShift.start_time
        .split(":")
        .map(Number);
      const [endHour, endMinute] = currentShift.end_time.split(":").map(Number);
      const startTimeMinutes = startHour * 60 + startMinute;
      const endTimeMinutes = endHour * 60 + endMinute;

      // Determine shift status
      if (currentTimeMinutes < startTimeMinutes) {
        // Before shift
        setShiftStatus("before");
        const minutesUntilShift = startTimeMinutes - currentTimeMinutes;
        const hours = Math.floor(minutesUntilShift / 60);
        const minutes = minutesUntilShift % 60;
        setShiftCountdown(`${t("shiftStartsIn")} ${hours}h ${minutes}m`);
      } else if (
        currentTimeMinutes >= startTimeMinutes &&
        currentTimeMinutes < endTimeMinutes
      ) {
        // During shift
        setShiftStatus("during");
        const minutesUntilEnd = endTimeMinutes - currentTimeMinutes;
        const hours = Math.floor(minutesUntilEnd / 60);
        const minutes = minutesUntilEnd % 60;
        setShiftCountdown(`${t("shiftEndsIn")} ${hours}h ${minutes}m`);
      } else {
        // After shift
        setShiftStatus("after");
        setShiftCountdown(t("shiftHasEnded"));
      }
    }, 1000);

    return () => clearInterval(intervalId);
  }, [currentShift, t]);

  const handleClockIn = async (type: AttendanceType = "work") => {
    try {
      await clockIn(staffId, storeId, type);
      toast({
        title: t("clockedInSuccess"),
        description: t("clockedInAt", { time: format(new Date(), "h:mm a") }),
      });
    } catch (error) {
      toast({
        title: t("errorClockingIn"),
        description: t("errorClockingInDescription"),
        variant: "destructive",
      });
    }
  };

  const handleClockOut = async () => {
    if (!currentAttendance) return;

    try {
      await clockOut(currentAttendance.id);
      toast({
        title: t("clockedOutSuccess"),
        description: t("clockedOutAt", { time: format(new Date(), "h:mm a") }),
      });
    } catch (error) {
      toast({
        title: t("errorClockingOut"),
        description: t("errorClockingOutDescription"),
        variant: "destructive",
      });
    }
  };

  // Get appropriate badge color based on attendance type
  const getTypeBadgeColor = (type: AttendanceType) => {
    switch (type) {
      case "work":
        return "bg-green-500 hover:bg-green-600";
      case "overtime":
        return "bg-amber-500 hover:bg-amber-600";
      case "holiday":
        return "bg-blue-500 hover:bg-blue-600";
      case "sick":
        return "bg-red-500 hover:bg-red-600";
      case "other":
        return "bg-gray-500 hover:bg-gray-600";
    }
  };

  return (
    <Card className="overflow-hidden">
      <CardHeader className="pb-2">
        <div className="flex justify-between items-center">
          <CardTitle className="flex items-center gap-2">
            <Clock className="h-5 w-5" />
            {t("attendanceClock")}
          </CardTitle>
          <div>
            {shiftStatus !== "none" && (
              <Badge
                className={
                  shiftStatus === "during"
                    ? "bg-green-600"
                    : shiftStatus === "before"
                    ? "bg-amber-600"
                    : "bg-slate-600"
                }
              >
                {shiftStatus === "during"
                  ? t("onShift")
                  : shiftStatus === "before"
                  ? t("upcomingShift")
                  : t("shiftEnded")}
              </Badge>
            )}
          </div>
        </div>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="flex flex-col items-center justify-center p-4 space-y-3">
          <div className="text-3xl font-bold text-center">
            {format(currentTime, "h:mm:ss a")}
          </div>
          <div className="text-sm text-muted-foreground">
            {format(currentTime, "EEEE, MMMM d, yyyy")}
          </div>

          {shiftCountdown && (
            <div
              className={`text-sm mt-1 font-medium ${
                shiftStatus === "during"
                  ? "text-green-600"
                  : shiftStatus === "before"
                  ? "text-amber-600"
                  : "text-slate-600"
              }`}
            >
              {shiftCountdown}
            </div>
          )}

          {currentAttendance && currentAttendance.clock_in && (
            <div className="space-y-2 w-full mt-2">
              <div className="flex justify-between items-center text-sm">
                <span className="font-semibold">{t("clockIn")}:</span>
                <span>
                  {format(new Date(currentAttendance.clock_in), "h:mm:ss a")}
                </span>
              </div>
              <div className="flex justify-between items-center text-sm">
                <span className="font-semibold">{t("status")}:</span>
                <Badge className={getTypeBadgeColor(currentAttendance.type)}>
                  {t(currentAttendance.type)}
                </Badge>
              </div>
              <div className="flex justify-between items-center text-sm">
                <span className="font-semibold">{t("elapsed")}:</span>
                <span className="font-mono">{elapsedTime}</span>
              </div>
            </div>
          )}
        </div>
      </CardContent>
      <CardFooter className="border-t bg-muted/50 p-3">
        <div className="w-full">
          {!currentAttendance ? (
            <div className="grid grid-cols-2 gap-2">
              <Button
                variant="default"
                className="w-full"
                onClick={() => handleClockIn("work")}
              >
                {t("clockIn")}
              </Button>
              <Button
                variant="secondary"
                className="w-full"
                onClick={() => handleClockIn("overtime")}
              >
                {t("overtime")}
              </Button>
            </div>
          ) : (
            <Button
              variant="destructive"
              className="w-full"
              onClick={handleClockOut}
            >
              {t("clockOut")}
            </Button>
          )}
        </div>
      </CardFooter>
    </Card>
  );
};
